import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import { Component, DestroyRef, effect, inject, input, type Signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { openDeleteLocationDialog } from '@app/modules/dashboard/components/confirm-delete-location-dialog/confirm-delete-location-dialog.component';
import { RestrictedElementDirective } from '@auth';
import { AppService, EntityStatus, sortUtils } from '@core';
import { IncidentSeverityCountComponent } from '@incidents';
import { UserRolesIds } from '@models';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@ngrx-store';
import { CdkVirtualScrollViewportWrapperComponent, ConfirmationDialogComponent, EntityStatusComponent, NoDataPlaceholderComponent, StatusCountComponent, VirtualTableScrollNormalizerDirective } from '@ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { type LocationData } from '../../../models';
import { LocationActions, LocationSelectors } from '../../../ngrx-store';
import { BuildingDataFilterService } from '../../../services/building-data-filter.service';

export const siteTableCurrentColumns = [
  'index',
  'friendlyName',
  'status',
  'address',
  'incidents',
  'deviceStatus',
  'action',
];
export const siteTableArchivedColumns = ['index', 'friendlyName', 'status', 'address', 'action'];

@Component({
  imports: [
    MatSort,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatHeaderCellDef,
    RouterLink,
    EntityStatusComponent,
    NgxSkeletonLoaderModule,
    RestrictedElementDirective,
    NgClass,
    NoDataPlaceholderComponent,
    MatRow,
    MatRowDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatTooltip,
    MatSortHeader,
    StatusCountComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    VirtualTableScrollNormalizerDirective,
    CdkVirtualScrollViewportWrapperComponent,
    MatTooltip,
    IncidentSeverityCountComponent,
  ],
  selector: 'avs-site-table',
  styleUrls: ['./site-table.component.scss'],
  templateUrl: './site-table.component.html',
})
export class SiteTableComponent {
  fillAvailableHeight = input(false);
  isInjected = input(false);
  tableId = input<string | null>(null);

  protected readonly eUserRolesIds = UserRolesIds;
  protected readonly eEntityStatus = EntityStatus;
  protected dataSource: MatTableDataSource<LocationData> = new MatTableDataSource<LocationData>([]);
  protected displayedColumns = siteTableCurrentColumns;
  protected buildingDataFilterService = inject(BuildingDataFilterService);
  protected locationsLoaded: Signal<boolean | undefined>;
  protected isLocationsSummariesLoading: Signal<boolean | undefined>;

  protected userRole: Signal<UserRolesIds | undefined>;

  private sort = viewChild(MatSort);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private appService = inject(AppService);

  constructor() {
    this.locationsLoaded = toSignal(this.store.select(LocationSelectors.areLocationsLoaded));
    this.isLocationsSummariesLoading = toSignal(this.store.select(LocationSelectors.isLocationsSummariesLoading));
    this.userRole = toSignal(this.store.select(AuthSelectors.userRole));

    this.buildingDataFilterService.filteredLocations$.pipe(takeUntilDestroyed()).subscribe(locations => {
      this.initTableData(locations);
    });

    this.buildingDataFilterService.buildingStatusFilter$.pipe(takeUntilDestroyed()).subscribe(value => {
      this.displayedColumns = value === 'current' ? siteTableCurrentColumns : siteTableArchivedColumns;
    });

    effect(() => {
      const sortEl = this.sort();

      if (sortEl) {
        this.dataSource.sort = sortEl;
      }
    });
  }

  protected deleteLocation(location: LocationData) {
    openDeleteLocationDialog(this.dialog, {
      locationId: location.id,
      name: location.friendlyName,
      navigateToParent: false,
    });
  }

  protected resumeSite(site: LocationData) {
    this.openConfirmationDialog({
      description: `Are you sure you want to resume the ${site.friendlyName}?`,
      site,
      status: EntityStatus.Active,
      title: 'Resume',
    });
  }

  protected pauseSite(site: LocationData) {
    this.openConfirmationDialog({
      description: `Are you sure you want to pause the ${site.friendlyName}?`,
      site,
      status: EntityStatus.Paused,
      title: 'Pause',
    });
  }

  protected archiveSite(site: LocationData) {
    this.openConfirmationDialog({
      description: `Are you sure you want to add ${site.friendlyName} to archive?`,
      site,
      status: EntityStatus.Archived,
      title: 'Add to Archive',
    });
  }

  protected restoreSite(site: LocationData) {
    this.openConfirmationDialog({
      description: `Are you sure you want to restore ${site.friendlyName} from archive?`,
      site,
      status: EntityStatus.Active,
      title: 'Restore',
    });
  }

  private initTableData(locations: LocationData[]) {
    const sort = this.sort();

    if (sort) {
      this.dataSource.data = locations;
      this.dataSource.sortData = sortUtils.naturalSorting;
    }
  }

  private openConfirmationDialog(
    {
      site,
      status,
      title,
      description,
    }: {
      description: string;
      site: LocationData;
      status: EntityStatus;
      title: string;
    }) {
    ConfirmationDialogComponent.open(this.dialog, {
      description,
      title,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmation => {
        if (confirmation) {
          this.store.dispatch(
            LocationActions.updateLocation({
              clientId: this.appService.currentClient,
              data: { status },
              locationId: site.id,
            }),
          );
        }
      });
  }
}
